/**
 * 63.11 (the owner, Oct 13: *"give small shallow craters to rocket impacts and smaller shallower to grenades, maybe a small
 * one for vehicle explosions as well"*; ARCHITECTURE §5.18, craterlets): small craters as person-scale ground beside the
 * digs. A shallow bowl with a low rim of spoil, by the burst and the ground's material (rock and ice take none); the fine
 * ground adds it (core/fineTerrain.ts personSurfaceRadius), so the feet, the eyes, sight, the bullets and the cover map
 * all read it. Made where the Match resolves a burst on the ground and where a vehicle is destroyed, behind
 * `GameConfig.smallCraters` (on unless false: off since the owner's Oct 13 "craterlets don't actually work in testing", on
 * again once the coarse readers carry them, DECISIONS 356 (2), 71.4 step 2), at most one planet change a tick: a tick's bursts land together
 * (`addCraterlets`). A burst inside a craterlet's bowl deepens it (to CRATERLET_CAP) rather than adding one, so the count
 * follows the ground fought over, not the shots. Each change is logged (a `TerrainEdit` of kind 'craterlet'), as a dig's.
 * Person heights throughout; pure.
 */
import type { Craterlet, GameState, GroundMaterial, PlanetState, TerrainEdit, Vec3 } from '../types';
import { vecAdd, vecNormalize, vecScale, vecSub } from '../math';
import { groundMaterial } from './groundMaterial';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dcos } from './dmath';

/** A craterlet's shape on soil (person heights): its bowl's radius and depth, its rim's height and how far out it runs. */
export const CRATERLETS: Record<Craterlet['kind'], { radius: number; depth: number; rim: number; rimOut: number }> = {
  rocket: { radius: 0.9, depth: 0.22, rim: 0.06, rimOut: 1.3 },
  grenade: { radius: 0.55, depth: 0.12, rim: 0.04, rimOut: 0.8 },
  vehicle: { radius: 1.1, depth: 0.18, rim: 0.05, rimOut: 1.5 },
};
/** By the ground: × the depth, × the width; null takes none (a scorch, the look's only). Catalogue dials. */
export const CRATERLET_GROUND: Record<GroundMaterial, { deep: number; wide: number } | null> = {
  soil: { deep: 1, wide: 1 }, mud: { deep: 1.15, wide: 1 }, ash: { deep: 1.2, wide: 1 }, sand: { deep: 1.4, wide: 1.2 },
  snow: { deep: 1.4, wide: 1.2 }, salt: { deep: 0.8, wide: 1 }, rock: null, ice: null, scree: null,
};
/** A craterlet is deepened by a later burst in its bowl by this much of its size, up to the cap. */
export const CRATERLET_DEEPEN = 0.2, CRATERLET_CAP = 1.6;
/** The farthest any craterlet's shape reaches from its centre (person heights): the widest rim, the widest ground, the cap. */
export const CRATERLET_REACH = Math.max(...Object.values(CRATERLETS).map(c => c.rimOut)) * 1.2 * Math.sqrt(CRATERLET_CAP);

/** Its size: the depth × scale, the widths × √scale (a deepened bowl widens less than it deepens), × the ground's (kept per craterlet). */
const sizes = new WeakMap<object, { radius: number; depth: number; rim: number; rimOut: number } | null>();
function sized(c: Pick<Craterlet, 'kind' | 'material' | 'scale'>): { radius: number; depth: number; rim: number; rimOut: number } | null {
  let s = sizes.get(c);
  if (s === undefined) { s = sizeOf(c); sizes.set(c, s); }
  return s;
}
function sizeOf(c: Pick<Craterlet, 'kind' | 'material' | 'scale'>): { radius: number; depth: number; rim: number; rimOut: number } | null {
  const g = CRATERLET_GROUND[c.material];
  if (!g) return null;
  const k = CRATERLETS[c.kind], w = g.wide * Math.sqrt(c.scale);
  return { radius: k.radius * w, depth: k.depth * g.deep * c.scale, rim: k.rim * g.deep * Math.sqrt(c.scale), rimOut: k.rimOut * w };
}

/** The craterlet's height (person heights, − below the ground) `r` person heights from its centre: the bowl, then the rim. */
export function craterletHeight(c: Pick<Craterlet, 'kind' | 'material' | 'scale'>, r: number): number {
  const s = sized(c);
  if (!s || r >= s.rimOut) return 0;
  if (r < s.radius) { const q = r / s.radius; return -s.depth * (1 - q * q); }
  const t = (r - s.radius) / (s.rimOut - s.radius);
  return s.rim * 4 * t * (1 - t);
}

/** Its shape as sized (person heights): bowl radius, depth, rim height, how far out; null on ground that takes none. */
export function craterletShape(c: Pick<Craterlet, 'kind' | 'material' | 'scale'>): { radius: number; depth: number; rim: number; rimOut: number } | null {
  return sized(c);
}

/** Its bowl's radius (person heights): a burst whose centre falls inside deepens it. */
export function craterletBowl(c: Pick<Craterlet, 'kind' | 'material' | 'scale'>): number {
  return sized(c)?.radius ?? 0;
}

/** A burst or a wreck that leaves a craterlet: its kind, its planet, the world point it happened at. */
export interface CraterletCause { kind: Craterlet['kind']; planetIndex: number; point: Vec3 }

/**
 * The tick's craterlets made: each cause on the ground's material at its point (none on rock or ice); one inside an existing
 * bowl deepens that one (to the cap), else a new one; each planet changed once, and each change logged. The state as it was
 * when none lands.
 */
export function addCraterlets(state: GameState, causes: readonly CraterletCause[], seconds: number): GameState {
  if (!causes.length || state.config.smallCraters === false) return state;
  const ps = personSetOf(state);
  if (!(ps.fineAmplitude > 0)) return state;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const lists = new Map<number, Craterlet[]>();
  const edits: TerrainEdit[] = [];
  for (const c of causes) {
    const planet = state.planets[c.planetIndex];
    if (!planet || !planet.alive) continue;
    const dir = vecNormalize(vecSub(c.point, planet.position));
    const material = groundMaterial(state, c.planetIndex, dir, seconds);
    if (!CRATERLET_GROUND[material]) continue;
    let list = lists.get(c.planetIndex);
    if (!list) { list = (planet.craterlets ?? []).slice(); lists.set(c.planetIndex, list); }
    const cosOf = (people: number) => dcos((people * person) / planet.radius);
    const host = list.findIndex(x => x.dir.x * dir.x + x.dir.y * dir.y + x.dir.z * dir.z >= cosOf(craterletBowl(x)));
    let made: Craterlet;
    if (host >= 0) {
      const was = list[host];
      if (was.scale >= CRATERLET_CAP) continue;
      made = { ...was, scale: Math.min(CRATERLET_CAP, was.scale + CRATERLET_DEEPEN) };
      list[host] = made;
    } else {
      made = { id: list.reduce((m, x) => Math.max(m, x.id), -1) + 1, kind: c.kind, material, dir, scale: 1 };
      list.push(made);
    }
    edits.push(craterletEdit(c.planetIndex, planet, made));
  }
  if (!edits.length) return state;
  const planets = state.planets.slice();
  for (const [pi, list] of lists) planets[pi] = { ...planets[pi], craterlets: list };
  return { ...state, planets, terrainEdits: [...(state.terrainEdits ?? []), ...edits] };
}

/** The log's record of a craterlet as it now is. */
export function craterletEdit(planetIndex: number, planet: PlanetState, c: Craterlet): TerrainEdit {
  return { kind: 'craterlet', planetIndex, planetId: planet.id, point: vecAdd(planet.position, vecScale(c.dir, planet.radius)), radius: 0, depth: 0, material: c.material, craterlet: { ...c, dir: { ...c.dir } } };
}

/** `planet` with `c` as it now is (it replaces an earlier record of the same one). */
export function withCraterlet(planet: PlanetState, c: Craterlet): PlanetState {
  const list = planet.craterlets ?? [], i = list.findIndex(x => x.id === c.id);
  return { ...planet, craterlets: i >= 0 ? list.map((x, j) => (j === i ? c : x)) : [...list, c] };
}
